import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useState } from "react";
import "../../styles/Home.css";
import { Email } from "@mui/icons-material";
import rebranable from "../../Assets/rebrandable.png";
import scalable from "../../Assets/scalable.png";
import secure from "../../Assets/secure.png";
import requireSign_video from "../../Assets/requireSign_video.mp4";
import Cards from "./Cards";
import { NavLink } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { try_for_free } from "../../url";

// Feature data for cleaner code
const FEATURES = [
  { icon: rebranable, label: "REBRANDABLE" },
  { icon: scalable, label: "SCALABLE" },
  { icon: secure, label: "SECURE" },
];

// Validation schema
const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
});

function Home() {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      if (!values.email) {
        setToastMessage("Email is required");
        setToastOpen(true);
        setSubmitting(false);
        return;
      }
      if (formik.errors.email) {
        setToastMessage("Enter a valid email");
        setToastOpen(true);
        setSubmitting(false);
        return;
      }
      window.location.href = `${try_for_free}?prefilled_email=${values.email}`;
    },
  });

  const handleToastClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToastOpen(false);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        maxWidth: {
          xs: "100%",
          sm: "90%",
          md: "85%",
          lg: "80%",
          xl: "75%",
        },
      }}
    >
      {/* Hero Section */}
      <Box
        id="home"
        pt={{ xs: 12, sm: 15, md: 18 }}
        sx={{
          background:
            "linear-gradient(135deg, rgba(22, 98, 167, 0.03) 0%, rgba(37, 178, 232, 0.03) 100%)",
          borderRadius: "30px",
          p: { xs: 2, md: 4 },
          mb: 4,
          mt: 15,
        }}
      >
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
          {/* Left Column - Text Content */}
          <Grid item xs={12} md={7} lg={6} sx={{ order: { xs: 1, md: 1 } }}>
            <Stack spacing={4}>
              {/* Main Heading */}
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "800",
                    fontSize: "clamp(2.2rem, 5.3vw, 4rem)",
                    lineHeight: { xs: 1.1, sm: 1.15, md: 1.2 },
                    color: "#1662A7",
                    textShadow: "0px 2px 4px rgba(22, 98, 167, 0.1)",
                    background:
                      "linear-gradient(135deg, #1662A7 0%, #25B2E8 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1,
                    textAlign: { xs: "center", md: "left" },
                    overflowWrap: "normal",
                  }}
                >
                  UNLIMITED
                  <br />
                  <Box component="span" sx={{ display: "inline-block", whiteSpace: "nowrap" }}>
                    E-SIGNATURES
                  </Box>
                </Typography>
                {/* Decorative underline */}
                <Box
                  sx={{
                    width: { xs: "120px", sm: "150px", md: "200px" },
                    height: "4px",
                    background:
                      "linear-gradient(90deg, #25B2E8 0%, #1662A7 100%)",
                    borderRadius: "2px",
                    mt: 1,
                    mx: { xs: "auto", md: 0 },
                  }}
                />
              </Box>

              {/* Subheading */}
              <Box
                sx={{
                  position: "relative",
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#5A6C7D",
                    fontSize: "clamp(1.25rem, 2.5vw, 2rem)",
                    fontWeight: "500",
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  at{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "#25B2E8",
                      fontSize: "clamp(1.4rem, 3.1vw, 2.4rem)",
                      fontWeight: "700",
                      textShadow: "0px 2px 4px rgba(37, 178, 232, 0.2)",
                    }}
                  >
                    AFFORDABLE PRICES
                  </Box>
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: "#5A6C7D",
                    fontSize: "clamp(1.25rem, 2.5vw, 2rem)",
                    fontWeight: "500",
                    lineHeight: 1.4,
                  }}
                >
                  for businesses{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "#1662A7",
                      fontSize: "clamp(1.4rem, 3.1vw, 2.4rem)",
                      fontWeight: "700",
                      textShadow: "0px 2px 4px rgba(22, 98, 167, 0.2)",
                    }}
                  >
                    WORLDWIDE
                  </Box>
                </Typography>
              </Box>

              {/* Features */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1.5, md: 2 },
                    justifyContent: { xs: "center", md: "flex-start" },
                    alignItems: "center",
                  }}
                >
                  {FEATURES.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: { xs: "44%", sm: "31%", md: "unset" },
                        flex: { xs: "1 1 44%", sm: "1 1 31%", md: "0 1 auto" },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent={{ xs: "center", md: "flex-start" }}
                        sx={{ px: { xs: 1, sm: 0 }, minWidth: 0 }}
                      >
                        <Avatar
                          variant="square"
                          src={feature.icon}
                          sx={{
                            width: "clamp(26px, 2.5vw, 40px)",
                            height: "clamp(26px, 2.5vw, 40px)",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: "600",
                            fontSize: "clamp(0.82rem, 1.4vw, 1.2rem)",
                            color: "#4A5568",
                            letterSpacing: "0.5px",
                            textAlign: { xs: "center", md: "left" },
                            whiteSpace: "nowrap",
                          }}
                        >
                          {feature.label}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          </Grid>

          {/* Right Column - Video */}
          <Grid item xs={12} md={5} lg={6} sx={{ order: { xs: 2, md: 2 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: { xs: 3, sm: 4, md: 0 },
                position: "relative",
                width: "100%",
                px: { xs: 1, sm: 2 },
              }}
            >
              {/* Decorative background elements */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  width: "100px",
                  height: "100px",
                  background:
                    "linear-gradient(135deg, rgba(37, 178, 232, 0.1) 0%, rgba(22, 98, 167, 0.1) 100%)",
                  borderRadius: "50%",
                  zIndex: 0,
                  display: { xs: "none", md: "block" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-30px",
                  left: "-30px",
                  width: "80px",
                  height: "80px",
                  background:
                    "linear-gradient(135deg, rgba(22, 98, 167, 0.1) 0%, rgba(37, 178, 232, 0.1) 100%)",
                  borderRadius: "50%",
                  zIndex: 0,
                  display: { xs: "none", md: "block" },
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  p: { xs: 0.5, sm: 1, md: 2 },
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)",
                  borderRadius: { xs: "15px", sm: "20px", md: "25px" },
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "500px", md: "600px" },
                }}
              >
                <Box
                  component="video"
                  controls
                  autoPlay
                  muted
                  sx={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "16/9",
                    minHeight: { xs: "180px", sm: "250px", md: "300px" },
                    maxHeight: { xs: "250px", sm: "350px", md: "400px" },
                    boxShadow: "0px 15px 50px rgba(0, 0, 0, 0.2)",
                    borderRadius: { xs: "12px", sm: "15px", md: "20px" },
                    transition: "all 0.4s ease",
                    objectFit: "cover",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.25)",
                    },
                  }}
                >
                  <source src={requireSign_video} type="video/mp4" />
                  Your browser does not support the video tag.
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Email Signup Section */}
        <Box sx={{ mt: { xs: 6, sm: 8, md: 10 }, mb: 4, px: { xs: 1, sm: 0 } }}>
          <form onSubmit={formik.handleSubmit}>
            <Box
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
                background:
                  "linear-gradient(135deg, #1662A7 0%, #25B2E8 50%, #1662A7 100%)",
                borderRadius: { xs: "20px", sm: "25px", md: "30px" },
                boxShadow: "0px 20px 60px rgba(22, 98, 167, 0.3)",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              {/* Title */}
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "800",
                    fontSize: "clamp(1.5rem, 3.2vw, 2.5rem)",
                    color: "#fff",
                    mb: 2,
                    textShadow: "0px 2px 8px rgba(0, 0, 0, 0.3)",
                    letterSpacing: "1px",
                  }}
                >
                  FREE 30 Day Trial
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: "400",
                    fontSize: "clamp(0.9rem, 1.6vw, 1.2rem)",
                    color: "rgba(255, 255, 255, 0.9)",
                    mb: { xs: 3, md: 4 },
                    maxWidth: "500px",
                    mx: "auto",
                    px: { xs: 2, sm: 0 },
                  }}
                >
                  Start your journey with unlimited e-signatures today!
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "clamp(0.9rem, 1.5vw, 1.1rem)",
                    color: "#FFFFFF",
                    mb: 1.25,
                  }}
                >
                  Unlimited documents & signatures - plans from $7.42/user/month.
                </Typography>
                <NavLink
                  to="/2024/pricing"
                  style={{
                    color: "rgba(255, 255, 255, 0.75)",
                    textDecoration: "none",
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "clamp(0.9rem, 1.35vw, 1rem)",
                  }}
                >
                  See pricing plans →
                </NavLink>
                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    mt: { xs: 2, md: 2.5 },
                    mb: { xs: 2.5, md: 3 },
                    maxWidth: "100%",
                  }}
                />
              </Box>

              {/* Email Input Container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  maxWidth: "650px",
                  mx: "auto",
                  p: 1.5,
                  backgroundColor: "#FFFFFF",
                  borderRadius: "20px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.1)",
                  backdropFilter: "blur(10px)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="you@email.com"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: "12px",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "2px solid #25B2E8",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "clamp(0.9rem, 1.3vw, 1rem)",
                      fontWeight: 500,
                      "&::placeholder": {
                        color: "#6B7280",
                        opacity: 1,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#6B7280", fontSize: "1.5rem" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Desktop Button */}
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    display: { xs: "none", md: "flex" },
                    backgroundColor: "#25B2E8",
                    color: "white",
                    fontFamily: "Roboto",
                    fontWeight: "bold",
                    fontSize: "clamp(0.9rem, 1.2vw, 1rem)",
                    textTransform: "capitalize",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    minWidth: "140px",
                    "&:hover": {
                      backgroundColor: "#1EA8D9",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Get Started
                </Button>
              </Box>

              {/* Mobile Button */}
              <Button
                type="submit"
                variant="contained"
                sx={{
                  display: { xs: "flex", md: "none" },
                  backgroundColor: "#25B2E8",
                  color: "white",
                  fontFamily: "Roboto",
                  fontWeight: "bold",
                  fontSize: "clamp(0.85rem, 2.8vw, 0.95rem)",
                  textTransform: "capitalize",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.2,
                  mt: 2,
                  mx: "auto",
                  "&:hover": {
                    backgroundColor: "#1EA8D9",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Started
              </Button>
            </Box>

            {/* Terms & Privacy */}
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                color: "#6E6B7B",
                fontSize: "clamp(0.85rem, 1.4vw, 1rem)",
                textAlign: "center",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              By clicking "Get Started" button,<br></br>you agree to{" "}
              <NavLink
                to="/2024/terms"
                style={{
                  color: "#1662A7",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                Terms & Conditions
              </NavLink>{" "}
              and{" "}
              <NavLink
                to="/2024/privacy"
                style={{
                  color: "#1662A7",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                Privacy Policy
              </NavLink>
              .
            </Typography>
          </form>
        </Box>
      </Box>

      <Cards />

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleToastClose}
          severity="error"
          sx={{
            width: "100%",
            backgroundColor: "#f44336",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Home;
